<template>
  <div v-if="shouldBlock" class="maintenance-overlay" role="dialog" aria-modal="true">
    <div class="panel">
      <div class="icon">
        <font-awesome-icon :icon="['fas', 'screwdriver-wrench']" />
      </div>
      <h2 class="title">{{ titleText }}</h2>
      <p class="message">
        {{ messageText }}
      </p>

      <a-spin v-if="loading" size="small" />

      <div class="actions">
        <a-button type="primary" disabled>{{ $t?.("global.ok") || "OK" }}</a-button>
        <a-button type="link" v-if="config?.adminBypassCode && !showBypass" @click="showBypass = true"
          >I'm an administrator</a-button
        >
      </div>
      <div v-if="showBypass" class="bypass">
        <div class="bypass-title">Administrator access</div>
        <a-input-password
          v-model:value="bypassInput"
          :placeholder="'Access code'"
          :disabled="verifying"
          @press-enter="verifyBypass" />
        <a-space style="margin-top: 10px">
          <a-button @click="closeBypass">Cancel</a-button>
          <a-button type="primary" :loading="verifying" @click="verifyBypass">Continue</a-button>
        </a-space>
        <div class="fineprint" style="margin-top: 8px">Bypass lasts {{ bypassMinutes }} minutes on this device.</div>
      </div>
      <div class="fineprint">If you need immediate assistance, please contact your administrator.</div>
    </div>
  </div>
</template>

<script setup>
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import { computed, onMounted, onUnmounted, ref } from "vue"

import reportError from "@/utils/reportError"

const config = ref(null)
const loading = ref(false)
const showBypass = ref(false)
const bypassInput = ref("")
const verifying = ref(false)

const BYPASS_STORAGE_KEY = "station.maintenance.bypassUntil"
const FETCH_INTERVAL_MS = 60_000
let intervalId = null

const titleText = computed(() => config.value?.title || "Scheduled Maintenance")
const messageText = computed(
  () =>
    config.value?.message || "We’re performing scheduled maintenance. Thanks for your patience — we’ll be back shortly."
)
const bypassMinutes = computed(() => Number(config.value?.bypassMinutes ?? 30))

const hasBypass = () => {
  try {
    const until = parseInt(localStorage.getItem(BYPASS_STORAGE_KEY) || "0", 10)
    return Number.isFinite(until) && Date.now() < until
  } catch (error) {
    reportError(error, { context: "MaintenanceOverlay.hasBypass" })
    return false
  }
}

const envMatches = computed(() => {
  const cfg = config.value
  if (!cfg) return false
  const list = (cfg.environments || ["development", "production"]).map((s) => String(s).toLowerCase())
  const current = (process.env.NODE_ENV === "production" ? "production" : "development").toLowerCase()
  const synonyms = new Map([
    ["dev", "development"],
    ["development", "development"],
    ["prod", "production"],
    ["production", "production"],
    ["*", current],
    ["all", current]
  ])
  const normalized = list.map((s) => synonyms.get(s) || s)
  return normalized.includes(current)
})

const shouldBlock = computed(() => {
  return Boolean(config.value?.enabled) && envMatches.value && !hasBypass()
})

const closeBypass = () => {
  showBypass.value = false
  bypassInput.value = ""
}

const grantBypass = () => {
  const until = Date.now() + bypassMinutes.value * 60 * 1000
  try {
    localStorage.setItem(BYPASS_STORAGE_KEY, String(until))
  } catch (error) {
    reportError(error, { context: "MaintenanceOverlay.grantBypass" })
  }
  window.dispatchEvent(new StorageEvent("storage", { key: BYPASS_STORAGE_KEY, newValue: String(until) }))
}

const verifyBypass = async () => {
  if (!config.value?.adminBypassCode) return
  verifying.value = true
  try {
    // Basic comparison (client-side only). For stronger security, enforce server-side checks.
    if ((bypassInput.value || "").trim() === String(config.value.adminBypassCode)) {
      grantBypass()
      closeBypass()
    }
  } finally {
    verifying.value = false
  }
}

const fetchConfig = async () => {
  loading.value = true
  try {
    const res = await fetch(`/maintenance.json?ts=${Date.now()}`, { cache: "no-store" })
    if (!res.ok) {
      // If file not found or error, assume maintenance disabled
      config.value = { enabled: false }
    } else {
      config.value = await res.json()
    }
  } catch (error) {
    reportError(error, { context: "MaintenanceOverlay.fetchConfig" })
    config.value = { enabled: false }
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  await fetchConfig()
  intervalId = window.setInterval(fetchConfig, FETCH_INTERVAL_MS)
  // Keep overlay state in sync across tabs
  window.addEventListener("storage", (e) => {
    if (e.key === BYPASS_STORAGE_KEY) {
      // Trigger reactive recompute
      // No-op body: computed should re-read localStorage on next access
    }
  })
})

onUnmounted(() => {
  if (intervalId) window.clearInterval(intervalId)
})
</script>

<style scoped lang="scss">
.maintenance-overlay {
  position: fixed;
  inset: 0;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(2px);
  z-index: 999998; /* below the dev badge */
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.panel {
  background: #ffffff;
  color: #2a2a2a;
  border-radius: 12px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.12);
  width: min(720px, 92vw);
  padding: 28px 32px;
  text-align: center;
}

.icon {
  font-size: 36px;
  color: var(--Primary, #1677ff);
  margin-bottom: 12px;
}

.title {
  margin: 0 0 8px 0;
}

.message {
  margin: 0 auto 18px auto;
  line-height: 1.6;
  max-width: 62ch;
}

.actions {
  margin-top: 8px;
}

.bypass {
  margin: 16px auto 0 auto;
  padding: 12px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fafafa;
  max-width: 420px;
}

.bypass-title {
  font-weight: 600;
  margin-bottom: 8px;
}

.fineprint {
  margin-top: 12px;
  font-size: 12px;
  color: #6b7280;
}
</style>
